import { useState, useEffect, useCallback } from "react";
import { Search, Pencil, KeyRound, Trash2, ShieldCheck, User as UserIcon, Loader2, LogIn, Star, Car as CarIcon, CalendarCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function MyAccountCard() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [emailPwd, setEmailPwd] = useState("");
  const [curPwd, setCurPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [busy, setBusy] = useState("");

  const saveName = async () => {
    setBusy("name");
    try { await api.put("/auth/profile", { name }); await refresh(); toast.success("Name updated."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const saveEmail = async () => {
    setBusy("email");
    try { await api.post("/auth/change-email", { new_email: email, current_password: emailPwd }); await refresh(); setEmailPwd(""); toast.success("Email updated."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const savePwd = async () => {
    setBusy("pwd");
    try { await api.post("/auth/change-password", { current_password: curPwd, new_password: newPwd }); setCurPwd(""); setNewPwd(""); toast.success("Password updated."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 max-w-2xl" data-testid="admin-my-account">
      <h3 className="font-heading text-lg font-semibold mb-1 flex items-center gap-2"><ShieldCheck size={18} className="text-blue-600" /> My admin login</h3>
      <p className="text-sm text-slate-500 mb-6">Change your own display name, login email, and password. Changes take effect immediately.</p>
      <div className="space-y-5">
        <Field label="Display name">
          <div className="flex gap-2">
            <input value={name} onChange={(e) => setName(e.target.value)} data-testid="my-name-input" className="belo-input w-full" />
            <button onClick={saveName} disabled={busy === "name"} data-testid="my-name-save" className="rounded-full bg-slate-900 text-white px-5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 whitespace-nowrap">Save</button>
          </div>
        </Field>
        <Field label="Login email">
          <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="my-email-input" className="belo-input w-full" />
          <div className="flex gap-2 mt-2">
            <input type="password" value={emailPwd} onChange={(e) => setEmailPwd(e.target.value)} data-testid="my-email-pwd" placeholder="Confirm current password" className="belo-input w-full" />
            <button onClick={saveEmail} disabled={busy === "email"} data-testid="my-email-save" className="rounded-full bg-slate-900 text-white px-5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 whitespace-nowrap">Update email</button>
          </div>
        </Field>
        <Field label="Change password">
          <input type="password" value={curPwd} onChange={(e) => setCurPwd(e.target.value)} data-testid="my-cur-pwd" placeholder="Current password" className="belo-input w-full" />
          <div className="flex gap-2 mt-2">
            <input type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="my-new-pwd" placeholder="New password (min 6 chars)" className="belo-input w-full" />
            <button onClick={savePwd} disabled={busy === "pwd"} data-testid="my-pwd-save" className="rounded-full bg-slate-900 text-white px-5 text-sm font-semibold hover:bg-slate-800 disabled:opacity-50 whitespace-nowrap">Update</button>
          </div>
        </Field>
      </div>
    </div>
  );
}

function EditUserModal({ target, onClose, onSaved }) {
  const { user: me } = useAuth();
  const isSuper = me?.role === "admin";
  const [name, setName] = useState(target.name || "");
  const [email, setEmail] = useState(target.email || "");
  const [role, setRole] = useState(target.role || "customer");
  const [isHost, setIsHost] = useState(!!target.is_host);
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/users/${target.id}`, { name, email, role, is_host: isHost });
      if (pwd) await api.post(`/admin/users/${target.id}/password`, { new_password: pwd });
      toast.success("User updated.");
      onSaved();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 grid place-items-center p-4" onClick={onClose} data-testid="edit-user-modal">
      <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-semibold mb-5">Edit user</h3>
        <div className="space-y-4">
          <Field label="Name"><input value={name} onChange={(e) => setName(e.target.value)} data-testid="edit-user-name" className="belo-input w-full" /></Field>
          <Field label="Email"><input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="edit-user-email" className="belo-input w-full" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Role">
              <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="edit-user-role" className="belo-input w-full" disabled={!isSuper && ["admin", "operations_manager"].includes(target.role)}>
                <option value="customer">Customer</option>
                {(isSuper || target.role === "operations_manager") && <option value="operations_manager">Operations Manager</option>}
                {(isSuper || target.role === "admin") && <option value="admin">Admin</option>}
              </select>
              {isSuper && <p className="text-xs text-slate-400 mt-1.5">Operations Managers get full operational access (view, edit & sign in as any account).</p>}
            </Field>
            <Field label="Host access">
              <label className="flex items-center gap-2 mt-2.5 text-sm">
                <input type="checkbox" checked={isHost} onChange={(e) => setIsHost(e.target.checked)} data-testid="edit-user-ishost" /> Can host cars
              </label>
            </Field>
          </div>
          <Field label="Reset password (optional)">
            <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="edit-user-pwd" placeholder="New password" className="belo-input w-full" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="rounded-full px-5 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="edit-user-save" className="rounded-full bg-blue-600 text-white px-5 py-2 text-sm font-semibold hover:bg-blue-700 disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

function UsersCard() {
  const [q, setQ] = useState("");
  const [users, setUsers] = useState(null);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const r = await api.get("/admin/users", { params: q ? { q } : {} });
    setUsers(r.data);
  }, [q]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const del = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("User deleted."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const impersonate = async (u) => {
    if (!window.confirm(`Log in as ${u.name || u.email}? You'll be signed in as this user (your admin session ends until you log back in).`)) return;
    try {
      await api.post(`/admin/users/${u.id}/impersonate`);
      toast.success(`Now signed in as ${u.name || u.email}`);
      window.location.assign(u.is_host || u.role !== "admin" ? "/host/dashboard" : "/");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const all = users || [];
  const counts = {
    all: all.length,
    host: all.filter((u) => u.is_host).length,
    customer: all.filter((u) => !u.is_host && u.role === "customer").length,
  };
  const shown = filter === "host" ? all.filter((u) => u.is_host)
    : filter === "customer" ? all.filter((u) => !u.is_host && u.role === "customer")
    : all;

  const Tab = ({ id, label }) => (
    <button onClick={() => setFilter(id)} data-testid={`users-filter-${id}`}
      className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${filter === id ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-800"}`}>
      {label} <span className={`ml-1 ${filter === id ? "text-blue-100" : "text-slate-400"}`}>{counts[id]}</span>
    </button>
  );

  const Rating = ({ r }) => (r?.count > 0
    ? <span className="inline-flex items-center gap-1 text-slate-700" title={`${r.count} review${r.count === 1 ? "" : "s"}`}><Star size={13} className="text-amber-400 fill-amber-400" /> {r.avg.toFixed(1)} <span className="text-slate-400">({r.count})</span></span>
    : <span className="text-slate-300">—</span>);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="admin-users">
      <div className="p-4 border-b border-slate-100 flex items-center gap-3 flex-wrap">
        <div className="inline-flex items-center bg-slate-100 rounded-full p-1" data-testid="users-filter-toggle">
          <Tab id="all" label="All" />
          <Tab id="customer" label="Customers" />
          <Tab id="host" label="Hosts" />
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search size={16} className="text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search" placeholder="Search by name or email…" className="flex-1 text-sm outline-none bg-transparent" />
        </div>
      </div>
      {users === null ? (
        <p className="p-8 text-center text-slate-400 flex items-center justify-center gap-2"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : shown.length === 0 ? (
        <p className="p-8 text-center text-slate-400" data-testid="users-empty">No users found.</p>
      ) : (
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-5 py-3 font-semibold">User</th>
              <th className="text-left px-5 py-3 font-semibold">Phone</th>
              <th className="text-left px-5 py-3 font-semibold">Role</th>
              <th className="text-left px-5 py-3 font-semibold">Activity</th>
              <th className="text-right px-5 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-slate-100">
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2.5">
                    <span className="grid place-items-center w-8 h-8 rounded-full bg-slate-100 text-slate-500">{(u.role === "admin" || u.role === "operations_manager") ? <ShieldCheck size={15} /> : <UserIcon size={15} />}</span>
                    <div><p className="font-medium text-slate-900">{u.name || "—"}</p><p className="text-slate-500 text-xs">{u.email}</p></div>
                  </div>
                </td>
                <td className="px-5 py-3 text-slate-600" data-testid={`user-phone-${u.id}`}>{u.phone || <span className="text-slate-300">—</span>}</td>
                <td className="px-5 py-3">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${u.role === "admin" ? "bg-blue-100 text-blue-700" : u.role === "operations_manager" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600"}`}>{u.role === "operations_manager" ? "operations manager" : u.role}</span>
                  {u.is_host && <span className="ml-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">host</span>}
                </td>
                <td className="px-5 py-3" data-testid={`user-activity-${u.id}`}>
                  {u.is_host ? (
                    <div className="flex items-center gap-3 text-xs">
                      <span className="inline-flex items-center gap-1 text-slate-700"><CarIcon size={13} className="text-slate-400" /> {u.vehicles_count} {u.vehicles_count === 1 ? "car" : "cars"}</span>
                      <Rating r={u.rating} />
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 text-xs">
                      <span className="inline-flex items-center gap-1 text-slate-700"><CalendarCheck size={13} className="text-slate-400" /> {u.trips_count} {u.trips_count === 1 ? "trip" : "trips"}</span>
                      <Rating r={u.rating} />
                    </div>
                  )}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center justify-end gap-1.5">
                    <button onClick={() => impersonate(u)} data-testid={`user-impersonate-${u.id}`} title="Log in as this user" className="p-2 rounded-lg text-slate-500 hover:bg-emerald-50 hover:text-emerald-600"><LogIn size={15} /></button>
                    <button onClick={() => setEditing(u)} data-testid={`user-edit-${u.id}`} title="Edit / set password" className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-blue-600"><Pencil size={15} /></button>
                    <button onClick={() => del(u)} data-testid={`user-delete-${u.id}`} title="Delete" className="p-2 rounded-lg text-slate-500 hover:bg-rose-50 hover:text-rose-600"><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {editing && <EditUserModal target={editing} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

export function AdminSettings() {
  return (
    <div className="space-y-6" data-testid="admin-settings">
      <MyAccountCard />
      <div>
        <h3 className="font-heading text-lg font-semibold mb-3 flex items-center gap-2"><KeyRound size={18} className="text-blue-600" /> All users & logins</h3>
        <UsersCard />
      </div>
    </div>
  );
}
